import React, { useState } from 'react';
import { Card } from 'react-bootstrap';
import { Briefcase, AlertCircle, DollarSign, Award, MessageSquare, CheckCircle, Users, Shield, TrendingUp, Star } from 'lucide-react';
import { FaExclamationTriangle } from 'react-icons/fa';

const InterviewGuide = () => {
  const [activeTab, setActiveTab] = useState('preparation');
  const [expandedSections, setExpandedSections] = useState({});

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  const preparationSteps = [
    {
      id: 1,
      icon: Briefcase,
      title: 'Zapoznanie z Firmą i Stanowiskiem',
      color: '#667eea',
      items: [
        {
          subtitle: 'Zrozumienie Biznesu',
          content: 'Sprawdź, czym dokładnie zajmuje się firma (misja, produkty, usługi). Poszukaj informacji o jej historii i największych sukcesach.'
        },
        {
          subtitle: 'Struktura i Wymagania',
          content: 'Zapoznaj się z ogólną strukturą firmy (czy jest duża, czy mała, kto jest Twoim potencjalnym przełożonym). Przeczytaj dokładnie opis stanowiska.'
        },
        {
          subtitle: 'Pytania do Pracodawcy',
          content: 'Przygotuj 2-3 przemyślane pytania. Pytaj o możliwości rozwoju, konkretne zadania, kulturę pracy.',
          example: 'Przykład: "W jaki sposób firma wspiera rozwój zawodowy pracowników na tym stanowisku?"'
        }
      ]
    },
    {
      id: 2,
      icon: Users,
      title: 'Wygląd i Punktualność',
      color: '#f093fb',
      items: [
        {
          subtitle: 'Ubiór (Dress Code)',
          content: 'Zawody techniczne/branżowe: Ciemne jeansy/spodnie materiałowe i czysta, wyprasowana koszula lub schludny sweter. Stanowiska biurowe: Garnitur/garsonka lub eleganckie spodnie i koszula/bluzka.'
        },
        {
          subtitle: 'Punktualność - Klucz do Eliminacji Stresu',
          content: 'Zaplanuj podróż dzień wcześniej. Sprawdź dokładny adres i najszybszą trasę. Wyjdź wcześniej - zaplanuj dotarcie 15-20 minut przed czasem. Wejdź do biura 5 minut przed umówioną godziną.'
        }
      ]
    },
    {
      id: 3,
      icon: MessageSquare,
      title: 'Język Ciała i Postawa',
      color: '#4facfe',
      items: [
        {
          subtitle: 'Kontakt Wzrokowy',
          content: 'Utrzymuj naturalny, pewny kontakt wzrokowy podczas rozmowy.'
        },
        {
          subtitle: 'Uścisk Dłoni',
          content: 'Pewny i mocny (ale nie miażdżący) uścisk dłoni na powitanie i pożegnanie.'
        },
        {
          subtitle: 'Słuchanie',
          content: 'Aktywnie słuchaj pytań. Przed odpowiedzią możesz zrobić krótką przerwę, by zebrać myśli.'
        }
      ]
    }
  ];

  const difficultQuestions = [
    {
      id: 1,
      question: 'Pytanie o Byłego Szefa (Tyran)',
      wrong: '"Mój szef był niekompetentny/ciągle krzyczał/wyzyskiwał mnie."',
      correct: '"Moja trzyletnia nauka zawodu dała mi solidne podstawy techniczne. Teraz szukam firmy, która oferuje większe możliwości rozwoju i w której panuje kultura pracy oparta na jasnej komunikacji i wzajemnym szacunku."',
      color: '#dc3545'
    },
    {
      id: 2,
      question: 'Pytanie o Odejście z Poprzedniej Pracy',
      wrong: '"Odszedłem/odeszłam, bo byłem/byłam zmęczony/a pracą po godzinach."',
      correct: '"Moja decyzja o zmianie wynika z chęci dalszego rozwoju w obszarze [Wymień Konkretny Obszar, np. obsługa nowych maszyn CNC] i poszukiwania środowiska, w którym będę mógł/mogła w pełni wykorzystać zdobyte umiejętności."',
      color: '#fd7e14'
    },
    {
      id: 3,
      question: 'Pytanie o Słabe Strony',
      wrong: 'Nie wymieniaj cech, które bezpośrednio dyskwalifikują Cię na danym stanowisku.',
      correct: '"Czasem zdarza mi się zbytnio koncentrować na detalach, co spowalnia moją pracę, ale świadomie nad tym pracuję, ustalając ścisłe priorytety. Moją główną słabością jest też niewielkie doświadczenie komercyjne – jest to coś, co zamierzam nadrobić w ciągu najbliższego roku intensywnej pracy."',
      color: '#ffc107'
    }
  ];

  const starExamples = [
    {
      id: 1,
      title: 'Dla Zawodu Technicznego/Produkcyjnego',
      focus: 'Rozwiązywanie problemów',
      color: '#667eea',
      response: '"Moim największym osiągnięciem było samodzielne rozwiązanie problemu technicznego podczas praktyki. W firmie [Nazwa], podczas pracy na stanowisku [Nazwa Stanowiska], zauważyłem/am, że [Wymień Wyzwanie]. Moja akcja polegała na przeanalizowaniu schematów elektrycznych/mechanicznych oraz testowaniu alternatywnych konfiguracji. Zamiast czekać na serwis, zaproponowałem/am i wdrożyłem/am proste usprawnienie procedury [Wymień Procedurę]. W rezultacie udało się skrócić czas przestoju o X godzin tygodniowo i uniknąć powtarzających się kosztów serwisu. To doświadczenie nauczyło mnie, że moje umiejętności techniczne mogą mieć bezpośredni, pozytywny wpływ na finanse firmy."'
    },
    {
      id: 2,
      title: 'Dla Zawodu Usługowego/Kreatywnego',
      focus: 'Samodzielność i Inicjatywa',
      color: '#43e97b',
      response: '"Za moje największe osiągnięcie uznaję samodzielne zaprojektowanie i wykonanie [Nazwa Produktu/Usługi] jako projekt dyplomowy/zaliczeniowy. Początkowo napotkałem/am trudności z [Wymień Trudność], ale stworzyłem/am własny harmonogram pracy i skonsultowałem/am się z zewnętrznym specjalistą w celu optymalizacji procesu. Efektem końcowym była praca, która zdobyła [Wymień Sukces] oraz została [Wymień Konkretny Zysk]. To osiągnięcie udowodniło mi, że potrafię zarządzać złożonym projektem i dostarczać wartość dodaną wykraczającą poza szkolne minimum."'
    }
  ];

  const starMethod = [
    {
      letter: 'S',
      title: 'Sytuacja',
      description: 'Opisz krótko tło, kontekst i wyzwanie, z jakim się zmierzyłeś/zmierzyłaś.',
      example: 'Przykład: "Podczas mojej praktyki w zakładzie [Nazwa Firmy] pojawił się problem z maszyną [Nazwa Maszyny], która często się zawieszała, spowalniając całą linię produkcyjną."'
    },
    {
      letter: 'T',
      title: 'Zadanie',
      description: 'Opisz cel, jaki musiałeś/aś osiągnąć.',
      example: 'Przykład: "Moim zadaniem było zdiagnozowanie problemu i zaproponowanie rozwiązania, które zapewni dłuższą, bezawaryjną pracę maszyny."'
    },
    {
      letter: 'A',
      title: 'Akcja',
      description: 'Opisz konkretne kroki, które podjąłeś/aś. Używaj słów: zdiagnozowałem, przeanalizowałem, zaproponowałem, wdrożyłem.',
      example: 'Przykład: "Zamiast tylko resetować maszynę, przeanalizowałem dokumentację techniczną i zauważyłem, że problem tkwi w nieodpowiedniej konfiguracji czujników temperatury. W porozumieniu z mistrzem, zaktualizowałem ustawienia i wprowadziłem procedurę regularnej kalibracji."'
    },
    {
      letter: 'R',
      title: 'Rezultat',
      description: 'Opisz mierzalny wynik Twoich działań. Używaj liczb i faktów.',
      example: 'Przykład: "W efekcie, w ciągu następnych dwóch tygodni, awaryjność maszyny spadła o 70%, co przełożyło się na wzrost efektywności produkcji i uniknięcie strat na poziomie około 5000 zł miesięcznie. To pokazało mi, jak ważna jest samodzielna analiza problemu."'
    }
  ];

  return (
    <div style={{
      minHeight: '100vh',
      paddingTop: '2rem',
      paddingBottom: '4rem',
      width: '100%'
    }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 1rem' }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
          <h1 style={{
            fontSize: '2.5rem',
            fontWeight: 'bold',
            color: 'white',
            marginBottom: '1rem'
          }}>
            Rozmowa Kwalifikacyjna
          </h1>
          <p style={{
            fontSize: '1.2rem',
            color: 'rgba(255,255,255,0.7)'
          }}>
            Kompletny przewodnik po Twojej pierwszej rozmowie kwalifikacyjnej
          </p>
        </div>

        {/* Tabs */}
        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '0.5rem',
          marginBottom: '2rem',
          background: 'rgba(255,255,255,0.1)',
          backdropFilter: 'blur(10px)',
          borderRadius: '20px',
          padding: '0.5rem'
        }}>
          {[
            { id: 'preparation', label: 'Przygotowanie', icon: CheckCircle },
            { id: 'questions', label: 'Trudne Pytania', icon: MessageSquare },
            { id: 'salary', label: 'Negocjacje', icon: DollarSign },
            { id: 'achievements', label: 'Metoda STAR', icon: Award }
          ].map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                style={{
                  flex: '1',
                  minWidth: '140px',
                  padding: '0.75rem 1rem',
                  borderRadius: '15px',
                  fontWeight: '600',
                  border: 'none',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem',
                  transition: 'all 0.3s',
                  background: activeTab === tab.id ? 'white' : 'transparent',
                  color: activeTab === tab.id ? '#667eea' : 'white',
                  boxShadow: activeTab === tab.id ? '0 4px 15px rgba(0,0,0,0.2)' : 'none'
                }}
              >
                <Icon size={18} />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Preparation Tab */}
        {activeTab === 'preparation' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {preparationSteps.map((step) => {
              const Icon = step.icon;
              const isExpanded = expandedSections[`prep-${step.id}`];
              
              return (
                <div
                  key={step.id}
                  style={{
                    background: 'rgba(255,255,255,0.1)',
                    backdropFilter: 'blur(10px)',
                    borderRadius: '20px',
                    border: '2px solid rgba(255,255,255,0.2)',
                    overflow: 'hidden',
                    transition: 'all 0.3s'
                  }}
                >
                  <button
                    onClick={() => toggleSection(`prep-${step.id}`)}
                    style={{
                      width: '100%',
                      padding: '1.5rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '1rem',
                      background: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      textAlign: 'left'
                    }}
                  >
                    <div style={{
                      width: '56px',
                      height: '56px',
                      borderRadius: '50%',
                      background: `linear-gradient(135deg, ${step.color} 0%, ${step.color}dd 100%)`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0
                    }}>
                      <Icon style={{ color: 'white' }} size={28} />
                    </div>
                    <h3 style={{
                      fontSize: '1.25rem',
                      fontWeight: 'bold',
                      color: 'white',
                      flex: 1
                    }}>
                      {step.title}
                    </h3>
                    <div style={{
                      color: 'white',
                      transform: isExpanded ? 'rotate(180deg)' : 'rotate(0)',
                      transition: 'transform 0.3s'
                    }}>
                      ▼
                    </div>
                  </button>
                  
                  {isExpanded && (
                    <div style={{ padding: '0 1.5rem 1.5rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                      {step.items.map((item, idx) => (
                        <div key={idx} style={{
                          background: 'rgba(255,255,255,0.05)',
                          borderRadius: '15px',
                          padding: '1rem',
                          border: '1px solid rgba(255,255,255,0.1)'
                        }}>
                          <h4 style={{
                            fontWeight: '600',
                            color: 'white',
                            marginBottom: '0.5rem'
                          }}>
                            {item.subtitle}
                          </h4>
                          <p style={{
                            color: 'rgba(255,255,255,0.8)',
                            lineHeight: '1.6',
                            margin: 0
                          }}>
                            {item.content}
                          </p>
                          {item.example && (
                            <div style={{
                              marginTop: '0.75rem',
                              padding: '0.75rem',
                              background: 'rgba(40, 167, 69, 0.2)',
                              border: '1px solid rgba(40, 167, 69, 0.3)',
                              borderRadius: '10px'
                            }}>
                              <p style={{
                                color: 'rgba(200, 255, 200, 1)',
                                fontSize: '0.9rem',
                                fontStyle: 'italic',
                                margin: 0
                              }}>
                                {item.example}
                              </p>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}

            {/* Additional Tips */}
            <div style={{
              background: 'linear-gradient(135deg, rgba(255, 193, 7, 0.1) 0%, rgba(255, 152, 0, 0.1) 100%)',
              border: '2px solid rgba(255, 193, 7, 0.3)',
              borderRadius: '20px',
              padding: '1.5rem',
              backdropFilter: 'blur(10px)'
            }}>
              <h3 style={{
                fontSize: '1.25rem',
                fontWeight: 'bold',
                color: 'white',
                marginBottom: '1rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}>
                <AlertCircle style={{ color: '#ffc107' }} />
                Inne Istotne Rady
              </h3>
              <ul style={{
                listStyle: 'none',
                padding: 0,
                margin: 0,
                display: 'flex',
                flexDirection: 'column',
                gap: '0.75rem'
              }}>
                <li style={{
                  display: 'flex',
                  gap: '0.75rem',
                  color: 'rgba(255,255,255,0.9)',
                  lineHeight: '1.6'
                }}>
                  <span style={{ color: '#ffc107', fontWeight: 'bold' }}>•</span>
                  <span>Przygotuj się na pytania o każdy punkt w Twoim CV, zwłaszcza o projekty szkolne lub zadania podczas praktyk</span>
                </li>
                <li style={{
                  display: 'flex',
                  gap: '0.75rem',
                  color: 'rgba(255,255,255,0.9)',
                  lineHeight: '1.6'
                }}>
                  <span style={{ color: '#ffc107', fontWeight: 'bold' }}>•</span>
                  <span>Zabierz notatnik i długopis. Robienie notatek pokazuje profesjonalizm i zaangażowanie</span>
                </li>
              </ul>
            </div>

            {/* BOTTOM INFO CARD */}
            <div
              className="mt-2"
            >
              <Card
                className="border-0 shadow-lg"
                style={{
                  borderRadius: '20px',
                  background: 'rgba(255, 0, 0, 0.1)', // delikatny czerwony
                  border: '2px solid rgba(255, 0, 0, 0.3)' // lekko czerwony obrys
                }}
              >
                <Card.Body className="p-4">
                  <h5 className="fw-bold mb-3">
                    <FaExclamationTriangle className="me-2" style={{ color: '#ff0000' }} />
                    MEGA WAŻNE
                  </h5>
                  <p style={{ fontSize: '1rem', lineHeight: '1.8', marginBottom: 0 }}>
                    Przygotuj kilka słów o sobie,  3 wady i 3 zalety oczywiście takie które można przekuć na sukces np. pracuje wolno ale jestem bardzo dokładny.
                  </p>
                </Card.Body>
              </Card>
            </div>
          </div>
        )}

        {/* Difficult Questions Tab */}
        {activeTab === 'questions' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <div style={{
              background: 'linear-gradient(135deg, rgba(220, 53, 69, 0.1) 0%, rgba(255, 0, 0, 0.1) 100%)',
              border: '2px solid rgba(220, 53, 69, 0.3)',
              borderRadius: '20px',
              padding: '1.5rem',
              backdropFilter: 'blur(10px)',
              marginBottom: '1rem'
            }}>
              <h3 style={{
                fontSize: '1.25rem',
                fontWeight: 'bold',
                color: 'white',
                marginBottom: '0.5rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}>
                <Shield style={{ color: '#ff6b6b' }} />
                Zasada Złota
              </h3>
              <p style={{
                color: 'rgba(255,255,255,0.9)',
                fontSize: '1.1rem',
                lineHeight: '1.6',
                margin: 0
              }}>
                Nigdy nie mów źle o byłym pracodawcy, nawet jeśli był tyranem. Zachowaj pełen profesjonalizm i dyplomację.
              </p>
            </div>

            {difficultQuestions.map((q) => {
              const isExpanded = expandedSections[`q-${q.id}`];
              
              return (
                <div
                  key={q.id}
                  style={{
                    background: 'rgba(255,255,255,0.1)',
                    backdropFilter: 'blur(10px)',
                    borderRadius: '20px',
                    border: `2px solid ${q.color}40`,
                    overflow: 'hidden'
                  }}
                >
                  <button
                    onClick={() => toggleSection(`q-${q.id}`)}
                    style={{
                      width: '100%',
                      padding: '1.5rem',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      background: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      textAlign: 'left'
                    }}
                  >
                    <h3 style={{
                      fontSize: '1.25rem',
                      fontWeight: 'bold',
                      color: 'white',
                      margin: 0
                    }}>
                      {q.question}
                    </h3>
                    <div style={{
                      color: 'white',
                      transform: isExpanded ? 'rotate(180deg)' : 'rotate(0)',
                      transition: 'transform 0.3s'
                    }}>
                      ▼
                    </div>
                  </button>
                  
                  {isExpanded && (
                    <div style={{ padding: '0 1.5rem 1.5rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                      <div style={{
                        background: 'rgba(220, 53, 69, 0.1)',
                        border: '2px solid rgba(220, 53, 69, 0.3)',
                        borderRadius: '15px',
                        padding: '1rem'
                      }}>
                        <h4 style={{
                          fontWeight: '600',
                          color: '#ff6b6b',
                          marginBottom: '0.5rem',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem'
                        }}>
                          ❌ Czego NIE mówić:
                        </h4>
                        <p style={{
                          color: 'rgba(255,255,255,0.8)',
                          lineHeight: '1.6',
                          margin: 0
                        }}>
                          {q.wrong}
                        </p>
                      </div>
                      
                      <div style={{
                        background: 'rgba(40, 167, 69, 0.1)',
                        border: '2px solid rgba(40, 167, 69, 0.3)',
                        borderRadius: '15px',
                        padding: '1rem'
                      }}>
                        <h4 style={{
                          fontWeight: '600',
                          color: '#51cf66',
                          marginBottom: '0.5rem',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem'
                        }}>
                          ✅ Dyplomatyczna odpowiedź:
                        </h4>
                        <p style={{
                          color: 'rgba(255,255,255,0.9)',
                          fontStyle: 'italic',
                          lineHeight: '1.8',
                          margin: 0
                        }}>
                          {q.correct}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Salary Tab */}
        {activeTab === 'salary' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            <div style={{
              background: 'rgba(255,255,255,0.1)',
              backdropFilter: 'blur(10px)',
              borderRadius: '20px',
              border: '2px solid rgba(255,255,255,0.2)',
              padding: '1.5rem'
            }}>
              <h3 style={{
                fontSize: '1.5rem',
                fontWeight: 'bold',
                color: 'white',
                marginBottom: '1rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}>
                <TrendingUp style={{ color: '#51cf66' }} />
                Przygotowanie: Ustal Zakres
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {[
                  'Minimalna Kwota (Must-Have): Najniższa kwota, za którą podejmiesz pracę',
                  'Kwota Rynkowa (Cel): Sprawdź widełki płacowe dla podobnych stanowisk juniorskich w Twojej lokalizacji',
                  'Kwota Optymalna (Negocjacyjna): Dodaj 10-15% do Kwoty Rynkowej - to będzie Twoja propozycja startowa'
                ].map((item, idx) => (
                  <div key={idx} style={{
                    background: 'rgba(255,255,255,0.05)',
                    borderRadius: '12px',
                    padding: '1rem',
                    border: '1px solid rgba(255,255,255,0.1)'
                  }}>
                    <p style={{
                      color: 'rgba(255,255,255,0.9)',
                      margin: 0,
                      lineHeight: '1.6'
                    }}>
                      {item}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            <div style={{
              background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(0, 86, 179, 0.1) 100%)',
              border: '2px solid rgba(102, 126, 234, 0.3)',
              borderRadius: '20px',
              padding: '1.5rem',
              backdropFilter: 'blur(10px)'
            }}>
              <h3 style={{
                fontSize: '1.25rem',
                fontWeight: 'bold',
                color: 'white',
                marginBottom: '1rem'
              }}>
                Twoja Odpowiedź (jeśli pytają pierwsi):
              </h3>
              <div style={{
                background: 'rgba(255,255,255,0.1)',
                borderRadius: '12px',
                padding: '1rem',
                marginBottom: '1rem'
              }}>
                <p style={{
                  color: 'rgba(255,255,255,0.9)',
                  fontStyle: 'italic',
                  lineHeight: '1.8',
                  margin: 0
                }}>
                  "Bazując na moich umiejętnościach zdobytych podczas 3 lat nauki zawodu, certyfikatach [Wymień Certyfikaty] oraz moich badaniach rynku pracy dla stanowisk juniorskich w tej okolicy, moje oczekiwania finansowe mieszczą się w przedziale [Dolna Granica] do [Górna Granica] brutto."
                </p>
              </div>
              
              <h4 style={{
                fontWeight: '600',
                color: 'white',
                marginBottom: '0.75rem'
              }}>
                Uzasadnienie (Kluczowe!):
              </h4>
              <div style={{
                background: 'rgba(255,255,255,0.1)',
                borderRadius: '12px',
                padding: '1rem'
              }}>
                <p style={{
                  color: 'rgba(255,255,255,0.9)',
                  fontStyle: 'italic',
                  lineHeight: '1.8',
                  margin: 0
                }}>
                  "Jest to kwota adekwatna, ponieważ wchodzę na rynek pracy z solidnymi podstawami teoretycznymi oraz praktyką zdobytą podczas stażu/praktyk, co minimalizuje czas potrzebny na moje wdrożenie w firmie."
                </p>
              </div>
            </div>

            <div style={{
              background: 'linear-gradient(135deg, rgba(253, 126, 20, 0.1) 0%, rgba(255, 152, 0, 0.1) 100%)',
              border: '2px solid rgba(253, 126, 20, 0.3)',
              borderRadius: '20px',
              padding: '1.5rem',
              backdropFilter: 'blur(10px)'
            }}>
              <h3 style={{
                fontSize: '1.25rem',
                fontWeight: 'bold',
                color: 'white',
                marginBottom: '1rem'
              }}>
                Asertywna Kontra (jeśli płaca jest za niska):
              </h3>
              <div style={{
                background: 'rgba(255,255,255,0.1)',
                borderRadius: '12px',
                padding: '1rem'
              }}>
                <p style={{
                  color: 'rgba(255,255,255,0.9)',
                  fontStyle: 'italic',
                  lineHeight: '1.8',
                  margin: 0
                }}>
                  "Rozumiem Pana/Pani propozycję. Uważam jednak, że moje umiejętności [np. samodzielna obsługa maszyny X, umiejętność Y] oraz 3-letnie zaangażowanie w naukę zawodu uzasadniają wyższe wynagrodzenie. Czy byłaby możliwość rozpoczęcia na poziomie [Minimalna Kwota +5%] z jasną ścieżką podwyżki po 6 miesiącach, po pełnym wdrożeniu?"
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Achievements Tab - STAR Method */}
        {activeTab === 'achievements' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            <div style={{
              background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)',
              border: '2px solid rgba(102, 126, 234, 0.3)',
              borderRadius: '20px',
              padding: '1.5rem',
              backdropFilter: 'blur(10px)'
            }}>
              <h3 style={{
                fontSize: '1.5rem',
                fontWeight: 'bold',
                color: 'white',
                marginBottom: '1rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}>
                <Star style={{ color: '#ffd700' }} />
                Metoda STAR
              </h3>
              <p style={{
                color: 'rgba(255,255,255,0.9)',
                lineHeight: '1.6',
                marginBottom: '1.5rem'
              }}>
                Pytanie o osiągnięcia to klasyczny element rozmowy kwalifikacyjnej. Pracodawca chce zobaczyć, czy potrafisz samodzielnie myśleć, rozwiązywać problemy i uczyć się na błędach. Najlepszym sposobem jest użycie metody STAR.
              </p>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {starMethod.map((step, idx) => (
                  <div key={idx} style={{
                    background: 'rgba(255,255,255,0.1)',
                    borderRadius: '15px',
                    padding: '1.25rem',
                    border: '2px solid rgba(255,255,255,0.2)'
                  }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '1rem',
                      marginBottom: '0.75rem'
                    }}>
                      <div style={{
                        width: '50px',
                        height: '50px',
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, #ffd700 0%, #ffed4e 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '1.5rem',
                        fontWeight: 'bold',
                        color: '#333',
                        flexShrink: 0
                      }}>
                        {step.letter}
                      </div>
                      <h4 style={{
                        fontSize: '1.25rem',
                        fontWeight: 'bold',
                        color: 'white',
                        margin: 0
                      }}>
                        {step.title}
                      </h4>
                    </div>
                    <p style={{
                      color: 'rgba(255,255,255,0.9)',
                      lineHeight: '1.6',
                      marginBottom: '0.75rem'
                    }}>
                      {step.description}
                    </p>
                    <div style={{
                      background: 'rgba(40, 167, 69, 0.2)',
                      border: '1px solid rgba(40, 167, 69, 0.3)',
                      borderRadius: '10px',
                      padding: '0.75rem'
                    }}>
                      <p style={{
                        color: 'rgba(200, 255, 200, 1)',
                        fontStyle: 'italic',
                        margin: 0,
                        lineHeight: '1.6'
                      }}>
                        {step.example}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <h3 style={{
              fontSize: '1.5rem',
              fontWeight: 'bold',
              color: 'white',
              marginTop: '1rem',
              marginBottom: '0.5rem'
            }}>
              Przykładowe Asertywne Odpowiedzi
            </h3>

            {starExamples.map((example) => {
              const isExpanded = expandedSections[`star-${example.id}`];
              
              return (
                <div
                  key={example.id}
                  style={{
                    background: 'rgba(255,255,255,0.1)',
                    backdropFilter: 'blur(10px)',
                    borderRadius: '20px',
                    border: '2px solid rgba(255,255,255,0.2)',
                    overflow: 'hidden'
                  }}
                >
                  <button
                    onClick={() => toggleSection(`star-${example.id}`)}
                    style={{
                      width: '100%',
                      padding: '1.5rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '1rem',
                      background: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      textAlign: 'left'
                    }}
                  >
                    <div style={{
                      width: '56px',
                      height: '56px',
                      borderRadius: '50%',
                      background: `linear-gradient(135deg, ${example.color} 0%, ${example.color}dd 100%)`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0
                    }}>
                      <Award style={{ color: 'white' }} size={28} />
                    </div>
                    <div style={{ flex: 1 }}>
                      <h3 style={{
                        fontSize: '1.25rem',
                        fontWeight: 'bold',
                        color: 'white',
                        margin: 0,
                        marginBottom: '0.25rem'
                      }}>
                        {example.title}
                      </h3>
                      <p style={{
                        color: 'rgba(255,255,255,0.7)',
                        fontSize: '0.9rem',
                        margin: 0
                      }}>
                        Focus: {example.focus}
                      </p>
                    </div>
                    <div style={{
                      color: 'white',
                      transform: isExpanded ? 'rotate(180deg)' : 'rotate(0)',
                      transition: 'transform 0.3s'
                    }}>
                      ▼
                    </div>
                  </button>
                  
                  {isExpanded && (
                    <div style={{ padding: '0 1.5rem 1.5rem' }}>
                      <div style={{
                        background: 'rgba(255,255,255,0.05)',
                        borderRadius: '15px',
                        padding: '1.25rem',
                        border: '1px solid rgba(255,255,255,0.1)'
                      }}>
                        <p style={{
                          color: 'rgba(255,255,255,0.95)',
                          fontStyle: 'italic',
                          lineHeight: '1.8',
                          margin: 0
                        }}>
                          {example.response}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}

            <div style={{
              background: 'linear-gradient(135deg, rgba(40, 167, 69, 0.1) 0%, rgba(25, 135, 84, 0.1) 100%)',
              border: '2px solid rgba(40, 167, 69, 0.3)',
              borderRadius: '20px',
              padding: '1.5rem',
              backdropFilter: 'blur(10px)',
              marginTop: '1rem'
            }}>
              <h3 style={{
                fontSize: '1.25rem',
                fontWeight: 'bold',
                color: 'white',
                marginBottom: '1rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}>
                <CheckCircle style={{ color: '#51cf66' }} />
                Kluczowe Wskazówki
              </h3>
              <ul style={{
                listStyle: 'none',
                padding: 0,
                margin: 0,
                display: 'flex',
                flexDirection: 'column',
                gap: '0.75rem'
              }}>
                <li style={{
                  display: 'flex',
                  gap: '0.75rem',
                  color: 'rgba(255,255,255,0.9)',
                  lineHeight: '1.6'
                }}>
                  <span style={{ color: '#51cf66', fontWeight: 'bold' }}>✓</span>
                  <span>Używaj konkretnych liczb i faktów w Rezultacie</span>
                </li>
                <li style={{
                  display: 'flex',
                  gap: '0.75rem',
                  color: 'rgba(255,255,255,0.9)',
                  lineHeight: '1.6'
                }}>
                  <span style={{ color: '#51cf66', fontWeight: 'bold' }}>✓</span>
                  <span>W Akcji używaj czasowników: przeanalizowałem, wdrożyłem, zaproponowałem</span>
                </li>
                <li style={{
                  display: 'flex',
                  gap: '0.75rem',
                  color: 'rgba(255,255,255,0.9)',
                  lineHeight: '1.6'
                }}>
                  <span style={{ color: '#51cf66', fontWeight: 'bold' }}>✓</span>
                  <span>Pokazuj, jak Twoje działania wpłynęły na finanse lub efektywność firmy</span>
                </li>
                <li style={{
                  display: 'flex',
                  gap: '0.75rem',
                  color: 'rgba(255,255,255,0.9)',
                  lineHeight: '1.6'
                }}>
                  <span style={{ color: '#51cf66', fontWeight: 'bold' }}>✓</span>
                  <span>Wybierz osiągnięcie, które najlepiej pokazuje Twoje umiejętności techniczne i miękkie</span>
                </li>
              </ul>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default InterviewGuide;